import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTypedSelector } from "../store";
import { AppLinkProps, getAppLinkProps } from "../utils/navigation/appLink";
import { TenantContextUrlOptions } from "../utils/navigation/buildTenantContextUrl";
import { resolveCurrentTenantId } from "../utils/navigation/tenantContext";

/**
 * `{ href, onClick }` for an in-app destination that must behave like a real link — middle click
 * and Cmd/Ctrl-click open a new tab in the SAME organization, right-click gets the browser's own
 * menu, plain left click stays a client-side navigation (NG-8661).
 *
 * Spread onto an `<a>` (or a MUI component rendered `component="a"`). Non-hook callers — AG Grid
 * cell renderers are plain functions, not components — use `getAppLinkProps` directly.
 */
export const useAppLink = (to: string, options?: TenantContextUrlOptions): AppLinkProps => {
    const navigate = useNavigate();
    // Redux is the FALLBACK, not the source. `?ctx` names the tab's routing context, and the
    // per-tab sessionStorage value is what writes and reads it (readAndScrubTenantContextFromUrl,
    // openAppInNewTab, the TokenStore slot key). Sourcing the href from Redux instead would give
    // one destination two sources of truth and point links at a different organization than the
    // page they sit on whenever the two diverge. resolveCurrentTenantId applies NG-8055's chain in
    // its documented order — sessionStorage → `/myself` → default — with the store's tenant as the
    // `/myself` term; the selector stays so links still recompute on a tenant switch.
    const myselfTenantId = useTypedSelector((state) => state.userReducer?.tenant?.id);
    const assume = options?.assume;

    return useMemo(
        () => getAppLinkProps(to, navigate, resolveCurrentTenantId(myselfTenantId), assume === undefined ? undefined : { assume }),
        [to, navigate, myselfTenantId, assume],
    );
};

export default useAppLink;
